import React, { useState, useEffect } from 'react';
import { Save, Building2, Upload, Plus, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const CompanySettings = () => {
  const [loading, setLoading] = useState(false);
  const [hasWhiteLabel, setHasWhiteLabel] = useState(false);
  const [checkingWhiteLabel, setCheckingWhiteLabel] = useState(true);
  const [settings, setSettings] = useState({
    company_name: '',
    company_address: '',
    company_phone: '',
    invoice_description: '',
    bank_accounts: [],
    auto_invoice_days: 5,
    disable_renewal_days: 5,
    logo_url: null,
    logo_invoice_url: null,
    logo_portal_url: null
  });

  useEffect(() => {
    fetchSettings();
    checkWhiteLabelSubscription();
  }, []);

  const checkWhiteLabelSubscription = async () => {
    try {
      const token = localStorage.getItem('token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      
      // Get user info from AuthContext or decode token to get tenant_id
      const response = await fetch(`${backendUrl}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const userData = await response.json();
      if (userData.success && userData.data.tenant_id) {
        // Check tenant's subscription features
        const tenantResponse = await fetch(`${backendUrl}/api/tenants/${userData.data.tenant_id}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        const tenantData = await tenantResponse.json();
        if (tenantData.success) {
          const features = tenantData.data?.subscription_plan?.features || [];
          const whitelabelEnabled = tenantData.data?.white_label_enabled || false;
          setHasWhiteLabel(features.includes('white_label') || whitelabelEnabled);
        }
      }
    } catch (error) {
      console.error('Failed to check white label subscription:', error);
      setHasWhiteLabel(false);
    } finally {
      setCheckingWhiteLabel(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      
      const response = await fetch(`${backendUrl}/api/company-settings`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      if (data.success) {
        setSettings(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      toast.error('Gagal memuat pengaturan perusahaan');
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      
      // Validate max 4 banks
      if (settings.bank_accounts.length > 4) {
        toast.error('Maksimal 4 rekening bank');
        setLoading(false);
        return;
      }
      
      const response = await fetch(`${backendUrl}/api/company-settings`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(settings)
      });
      
      const data = await response.json();
      if (data.success) {
        toast.success('Pengaturan berhasil disimpan! Kembali ke dashboard...');
        
        // Redirect to dashboard after 1 second
        setTimeout(() => {
          window.location.href = '/app/dashboard';
        }, 1000);
      } else {
        toast.error('Gagal menyimpan pengaturan');
        setLoading(false);
      }
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Gagal menyimpan pengaturan');
      setLoading(false);
    }
  };

  const handleLogoUpload = (e) => {
    // Check if white label is enabled
    if (!hasWhiteLabel) {
      toast.error('Upload logo memerlukan fitur White Label. Silakan upgrade subscription Anda.');
      e.target.value = ''; // Reset file input
      return;
    }
    
    const file = e.target.files[0];
    if (file) {
      // Convert to base64
      const reader = new FileReader();
      reader.onloadend = () => {
        setSettings({...settings, logo_url: reader.result});
        toast.success('Logo berhasil diupload');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleInvoiceLogoUpload = (e) => {
    // Check if white label is enabled
    if (!hasWhiteLabel) {
      toast.error('Upload logo memerlukan fitur White Label. Silakan upgrade subscription Anda.');
      e.target.value = ''; // Reset file input
      return;
    }
    
    const file = e.target.files[0];
    if (file) {
      // Convert to base64
      const reader = new FileReader();
      reader.onloadend = () => {
        setSettings({...settings, logo_invoice_url: reader.result});
        toast.success('Logo Invoice berhasil diupload');
      };
      reader.readAsDataURL(file);
    }
  };

  const handlePortalLogoUpload = (e) => {
    // Check if white label is enabled
    if (!hasWhiteLabel) {
      toast.error('Upload logo memerlukan fitur White Label. Silakan upgrade subscription Anda.');
      e.target.value = ''; // Reset file input
      return;
    }
    
    const file = e.target.files[0];
    if (file) {
      // Convert to base64
      const reader = new FileReader();
      reader.onloadend = () => {
        setSettings({...settings, logo_portal_url: reader.result});
        toast.success('Logo Portal Pelanggan berhasil diupload');
      };
      reader.readAsDataURL(file);
    }
  };

  const addBankAccount = () => {
    if (settings.bank_accounts.length >= 4) {
      toast.error('Maksimal 4 rekening bank');
      return;
    }
    setSettings({
      ...settings,
      bank_accounts: [
        ...settings.bank_accounts,
        { bank_name: '', account_name: '', account_number: '' }
      ]
    });
  };

  const removeBankAccount = (index) => {
    const newBanks = settings.bank_accounts.filter((_, i) => i !== index);
    setSettings({...settings, bank_accounts: newBanks});
  };

  const updateBankAccount = (index, field, value) => {
    const newBanks = [...settings.bank_accounts];
    newBanks[index][field] = value;
    setSettings({...settings, bank_accounts: newBanks});
  };

  return (
    <div className="space-y-6 animate-fade-in p-4 min-h-screen bg-slate-50 dark:bg-slate-900">
      <Card className="border-0 shadow-lg bg-white dark:bg-slate-800">
        <CardHeader className="border-b border-slate-200 dark:border-slate-700">
          <CardTitle className="flex items-center space-x-2 text-slate-900 dark:text-white">
            <Building2 className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
            <span>Informasi Perusahaan</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="space-y-6">
            {/* Logo Upload */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2 text-slate-700 dark:text-slate-200">
                Logo Perusahaan
                {!checkingWhiteLabel && !hasWhiteLabel && (
                  <span className="text-xs bg-amber-100 dark:bg-amber-900/50 text-amber-800 dark:text-amber-300 px-2 py-0.5 rounded-full font-medium">
                    Memerlukan White Label
                  </span>
                )}
              </Label>
              <div className="flex items-center space-x-4">
                {settings.logo_url && (
                  <img 
                    src={settings.logo_url} 
                    alt="Company Logo" 
                    className="h-20 w-20 object-contain border dark:border-slate-600 rounded"
                  />
                )}
                <div className="space-y-2">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="hidden"
                    id="logo-upload"
                    disabled={!hasWhiteLabel}
                  />
                  <label htmlFor="logo-upload">
                    <Button
                      type="button"
                      variant="outline"
                      className={hasWhiteLabel ? "cursor-pointer" : "cursor-not-allowed opacity-50"}
                      onClick={() => {
                        if (!hasWhiteLabel) {
                          toast.error('Upload logo memerlukan fitur White Label. Hubungi admin untuk upgrade.');
                          return;
                        }
                        document.getElementById('logo-upload').click();
                      }}
                      disabled={!hasWhiteLabel}
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Upload Logo
                    </Button>
                  </label>
                  {hasWhiteLabel ? (
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                      Logo akan tampil di header aplikasi dan navbar
                    </p>
                  ) : (
                    <p className="text-sm text-amber-600 dark:text-amber-400 mt-1">
                      ⚠️ Fitur White Label diperlukan untuk menggunakan logo kustom. Hubungi admin untuk upgrade subscription.
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Invoice Logo Upload - Separate from main logo */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2 text-slate-700 dark:text-slate-200">
                Logo Invoice (Khusus Cetak)
                {!checkingWhiteLabel && !hasWhiteLabel && (
                  <span className="text-xs bg-amber-100 dark:bg-amber-900/50 text-amber-800 dark:text-amber-300 px-2 py-0.5 rounded-full font-medium">
                    Memerlukan White Label
                  </span>
                )}
              </Label>
              <div className="flex items-center space-x-4">
                {settings.logo_invoice_url && (
                  <img 
                    src={settings.logo_invoice_url} 
                    alt="Invoice Logo" 
                    className="h-20 w-20 object-contain border dark:border-slate-600 rounded bg-white dark:bg-slate-700"
                  />
                )}
                <div className="space-y-2">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleInvoiceLogoUpload}
                    className="hidden"
                    id="invoice-logo-upload"
                    disabled={!hasWhiteLabel}
                  />
                  <label htmlFor="invoice-logo-upload">
                    <Button
                      type="button"
                      variant="outline"
                      className={hasWhiteLabel ? "cursor-pointer" : "cursor-not-allowed opacity-50"}
                      onClick={() => {
                        if (!hasWhiteLabel) {
                          toast.error('Upload logo memerlukan fitur White Label. Hubungi admin untuk upgrade.');
                          return;
                        }
                        document.getElementById('invoice-logo-upload').click();
                      }}
                      disabled={!hasWhiteLabel}
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Upload Logo Invoice
                    </Button>
                  </label>
                  {hasWhiteLabel ? (
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                      Logo ini khusus untuk cetak invoice (gunakan warna gelap agar terlihat di kertas putih)
                    </p>
                  ) : (
                    <p className="text-sm text-amber-600 dark:text-amber-400 mt-1">
                      ⚠️ Fitur White Label diperlukan untuk menggunakan logo kustom.
                    </p>
                  )}
                </div>
              </div>
              
              {/* Logo Portal Pelanggan */}
              <div className="space-y-4">
                <p className="text-sm font-medium text-slate-600 dark:text-slate-400">
                  Logo Portal Pelanggan
                </p>
                {settings.logo_portal_url && (
                  <img 
                    src={settings.logo_portal_url} 
                    alt="Logo Portal" 
                    className="max-h-24 object-contain border rounded-lg p-2 bg-white dark:bg-slate-800"
                  />
                )}
                <div className="space-y-2">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handlePortalLogoUpload}
                    className="hidden"
                    id="portal-logo-upload"
                    disabled={!hasWhiteLabel}
                  />
                  <label htmlFor="portal-logo-upload">
                    <Button
                      type="button"
                      variant="outline"
                      className={hasWhiteLabel ? "cursor-pointer" : "cursor-not-allowed opacity-50"}
                      onClick={() => {
                        if (!hasWhiteLabel) {
                          toast.error('Upload logo memerlukan fitur White Label. Hubungi admin untuk upgrade.');
                          return;
                        }
                        document.getElementById('portal-logo-upload').click();
                      }}
                      disabled={!hasWhiteLabel}
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Upload Logo Portal
                    </Button>
                  </label>
                  {hasWhiteLabel ? (
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                      Logo ini akan ditampilkan di halaman portal pelanggan
                    </p>
                  ) : (
                    <p className="text-sm text-amber-600 dark:text-amber-400 mt-1">
                      ⚠️ Fitur White Label diperlukan untuk menggunakan logo kustom.
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Company Name */}
            <div className="space-y-2">
              <Label>
                <span className="text-red-500">! </span>Perusahaan
              </Label>
              <Input
                value={settings.company_name}
                onChange={(e) => setSettings({...settings, company_name: e.target.value})}
                placeholder="Nama Perusahaan"
              />
            </div>

            {/* Address */}
            <div className="space-y-2">
              <Label>
                <span className="text-red-500">! </span>Alamat
              </Label>
              <Textarea
                value={settings.company_address}
                onChange={(e) => setSettings({...settings, company_address: e.target.value})}
                placeholder="Telaga Golf Sawangan Cluster BelandaBlok E10 No.61 Sawangan Depok Jawa Barat 16511"
                rows={3}
              />
            </div>

            {/* Phone */}
            <div className="space-y-2">
              <Label>
                <span className="text-red-500">! </span>Nomor HP
              </Label>
              <Input
                value={settings.company_phone}
                onChange={(e) => setSettings({...settings, company_phone: e.target.value})}
                placeholder="08888200888"
              />
            </div>

            {/* Invoice Description */}
            <div className="space-y-2">
              <Label>
                <span className="text-red-500">! </span>Deskripsi Invoice
              </Label>
              <Input
                value={settings.invoice_description}
                onChange={(e) => setSettings({...settings, invoice_description: e.target.value})}
                placeholder="High Speed Internet Package Service"
              />
            </div>

            {/* Bank Accounts */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold text-slate-700 dark:text-slate-200">Rekening Bank</Label>
                <span className="text-sm text-slate-500 dark:text-slate-400">
                  {settings.bank_accounts.length} Rekening Bank
                </span>
              </div>

              {settings.bank_accounts.map((bank, index) => (
                <Card key={index} className="border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
                  <CardContent className="pt-6">
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <Label className="font-semibold text-slate-700 dark:text-slate-200">Bank {index + 1}</Label>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeBankAccount(index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label>Nama Bank</Label>
                          <Input
                            value={bank.bank_name}
                            onChange={(e) => updateBankAccount(index, 'bank_name', e.target.value)}
                            placeholder="Bank Mandiri"
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label>Atas Nama</Label>
                          <Input
                            value={bank.account_name}
                            onChange={(e) => updateBankAccount(index, 'account_name', e.target.value)}
                            placeholder="PT. PUSAKA KREASI MANDIRI"
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label>No. Rekening</Label>
                          <Input
                            value={bank.account_number}
                            onChange={(e) => updateBankAccount(index, 'account_number', e.target.value)}
                            placeholder="164-0004110682"
                          />
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}

              {settings.bank_accounts.length < 4 && (
                <Button
                  variant="outline"
                  onClick={addBankAccount}
                  className="w-full"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Tambah Rekening Bank
                </Button>
              )}
            </div>

            {/* Auto Invoice Settings */}
            <div className="space-y-2">
              <Label>Invoice Otomatis</Label>
              <select
                value={settings.auto_invoice_days}
                onChange={(e) => setSettings({...settings, auto_invoice_days: parseInt(e.target.value)})}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-slate-700 dark:text-white"
              >
                <option value={5}>5 HARI SEBELUM JATUH TEMPO</option>
                <option value={7}>7 HARI SEBELUM JATUH TEMPO</option>
                <option value={10}>10 HARI SEBELUM JATUH TEMPO</option>
                <option value={15}>15 HARI SEBELUM JATUH TEMPO</option>
                <option value={30}>30 HARI SEBELUM JATUH TEMPO</option>
              </select>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                INVOICE BELUM BAYAR PADA DATA PELANGGAN (HOTSPOT / PPP) HARUS SUDAH TERBAYARKAN SEBELUM PERIODE YANG DIPILIH
              </p>
            </div>

            {/* Disable Renewal */}
            <div className="space-y-2">
              <Label>Disable Perpanjangan Paket Sampai</Label>
              <select
                value={settings.disable_renewal_days}
                onChange={(e) => setSettings({...settings, disable_renewal_days: parseInt(e.target.value)})}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-slate-700 dark:text-white"
              >
                <option value={5}>5 HARI SEBELUM JATUH TEMPO</option>
                <option value={7}>7 HARI SEBELUM JATUH TEMPO</option>
                <option value={10}>10 HARI SEBELUM JATUH TEMPO</option>
                <option value={15}>15 HARI SEBELUM JATUH TEMPO</option>
                <option value={30}>30 HARI SEBELUM JATUH TEMPO</option>
              </select>
            </div>
          </div>

          <div className="flex justify-end mt-6 space-x-3">
            <Button
              variant="outline"
              onClick={() => window.history.back()}
            >
              Kembali
            </Button>
            <Button
              className="bg-gradient-to-r from-indigo-600 to-purple-600"
              onClick={handleSave}
              disabled={loading}
            >
              <Save className="w-4 h-4 mr-2" />
              {loading ? 'Menyimpan...' : 'Simpan Perubahan'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="text-center text-sm text-slate-500 dark:text-slate-400">
        Copyright 2025 © ISP Billing V1.0
      </div>
    </div>
  );
};

export default CompanySettings;
